/**
 * Color utility functions for ensuring proper text contrast
 */

/**
 * Convert hex color to RGB
 */
function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
}

/**
 * Calculate relative luminance of a color
 * https://www.w3.org/TR/WCAG20/#relativeluminancedef
 */
function getLuminance(r: number, g: number, b: number): number {
  const [rs, gs, bs] = [r, g, b].map((c) => {
    const sRGB = c / 255;
    return sRGB <= 0.03928 ? sRGB / 12.92 : Math.pow((sRGB + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

/**
 * Calculate contrast ratio between two colors
 * https://www.w3.org/TR/WCAG20/#contrast-ratiodef
 */
export function getContrastRatio(color1: string, color2: string): number {
  const rgb1 = hexToRgb(color1);
  const rgb2 = hexToRgb(color2);

  if (!rgb1 || !rgb2) return 1;

  const lum1 = getLuminance(rgb1.r, rgb1.g, rgb1.b);
  const lum2 = getLuminance(rgb2.r, rgb2.g, rgb2.b);

  const lighter = Math.max(lum1, lum2);
  const darker = Math.min(lum1, lum2);

  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Get text color (black or white) that provides best contrast with background
 * @param backgroundColor - Hex color code (e.g., '#ff0000')
 * @returns '#000000' for dark text or '#ffffff' for light text
 */
export function getContrastTextColor(backgroundColor: string): string {
  const rgb = hexToRgb(backgroundColor);
  if (!rgb) return '#000000';

  const luminance = getLuminance(rgb.r, rgb.g, rgb.b);

  // If luminance > 0.5, background is light, use dark text
  // If luminance <= 0.5, background is dark, use light text
  return luminance > 0.5 ? '#000000' : '#ffffff';
}

/**
 * Check if text color has sufficient contrast with background
 * @param textColor - Hex color code
 * @param backgroundColor - Hex color code
 * @param level - WCAG level ('AA' or 'AAA')
 * @returns true if contrast is sufficient
 */
export function hasGoodContrast(
  textColor: string,
  backgroundColor: string,
  level: 'AA' | 'AAA' = 'AA'
): boolean {
  const ratio = getContrastRatio(textColor, backgroundColor);
  const minRatio = level === 'AAA' ? 7 : 4.5;
  return ratio >= minRatio;
}

/**
 * Get fiber color hex code
 */
export function getFiberColorHex(color: string): string {
  // Normalize color name (remove "-čierna" suffix and convert to lowercase)
  let normalizedColor = color.toLowerCase();

  // Handle colors with "-čierna" suffix (e.g., "Modro-čierna" -> "modrá")
  if (normalizedColor.includes('-čierna') || normalizedColor.includes('-priesvitná')) {
    normalizedColor = normalizedColor.split('-')[0] + 'á';
  }

  const colorMap: Record<string, string> = {
    // English names (legacy support)
    blue: '#0000FF',
    orange: '#FF8C00',
    green: '#008000',
    brown: '#8B4513',
    slate: '#808080',
    white: '#FFFFFF',
    red: '#FF0000',
    black: '#000000',
    yellow: '#FFFF00',
    violet: '#800080',
    rose: '#FFC0CB',
    aqua: '#40E0D0',

    // Slovak names (primary)
    'modrá': '#0000FF',
    'oranžová': '#FF8C00',
    'zelená': '#008000',
    'hnedá': '#8B4513',
    'šedá': '#808080',
    'biela': '#FFFFFF',
    'červená': '#FF0000',
    'čierna': '#000000',
    'žltá': '#FFFF00',
    'fialová': '#800080',
    'ružová': '#FFC0CB',
    'tyrkysová': '#40E0D0',
    'priesvitná': '#E0E0E0',
  };

  return colorMap[normalizedColor] || '#95a5a6';
}

/**
 * Get fiber badge style with proper contrast
 */
export function getFiberBadgeStyle(color: string): {
  backgroundColor?: string;
  backgroundImage?: string;
  backgroundSize?: string;
  backgroundPosition?: string;
  backgroundRepeat?: string;
  color: string;
} {
  const lowerColor = color.toLowerCase();

  // Handle colors with "-čierna" suffix (e.g., "Modro-čierna")
  if (lowerColor.includes('-čierna')) {
    // Extract base color: "modro-čierna" -> "modrá"
    const basePart = lowerColor.split('-')[0]; // "modro"
    // Replace last 'o' with 'á': "modro" -> "modrá", "zeleno" -> "zelená"
    const baseColorName = basePart.replace(/o$/, 'á');
    const baseColor = getFiberColorHex(baseColorName);

    // Create horizontal dashed black line through the middle
    // Using repeating gradient to create dashed effect
    const backgroundImage = `repeating-linear-gradient(
      90deg,
      #000000 0px,
      #000000 6px,
      transparent 6px,
      transparent 12px
    )`;

    return {
      backgroundColor: baseColor,
      backgroundImage,
      backgroundSize: '100% 2px',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      color: '#FFFFFF', // White text for better contrast
    };
  }

  // Handle "Čierno-priesvitná" (transparent with black dashed line)
  if (lowerColor.includes('čierno-priesvitná') || lowerColor.includes('cierno-priesvitna')) {
    const transparentColor = '#E0E0E0';

    const backgroundImage = `repeating-linear-gradient(
      90deg,
      #000000 0px,
      #000000 6px,
      transparent 6px,
      transparent 12px
    )`;

    return {
      backgroundColor: transparentColor,
      backgroundImage,
      backgroundSize: '100% 2px',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      color: '#000000',
    };
  }

  // Standard solid color
  const backgroundColor = getFiberColorHex(color);
  const textColor = getContrastTextColor(backgroundColor);

  return {
    backgroundColor,
    color: textColor,
  };
}


/**
 * Farba (hex) vlákna podľa jeho čísla — štandardné poradie GPON vlákien (1–12, opakuje sa).
 * #1 modrá, #2 oranžová, #3 zelená, #4 hnedá, #5 šedá, #6 biela,
 * #7 červená, #8 čierna, #9 žltá, #10 fialová, #11 ružová, #12 tyrkysová.
 */
const FIBER_HEX_BY_NUMBER = [
  '#2196F3', '#FF9800', '#4CAF50', '#795548', '#9E9E9E', '#FAFAFA',
  '#F44336', '#212121', '#FFEB3B', '#9C27B0', '#E91E63', '#009688',
];
export function getFiberHexByNumber(fiberNumber?: number | null): string | null {
  if (!fiberNumber || fiberNumber < 1) return null;
  return FIBER_HEX_BY_NUMBER[((fiberNumber - 1) % 12 + 12) % 12];
}
